import { AccessToken, OktaAuth, UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";

export enum UserRole {
  Internal = "Internal User",
  External = "External User",
  NoRole = "",
  DeliveriesAdmin = "Deliveries Admin"
}

export const AUTH_CLAIM_REAUTH_ATTEMPTS_KEY =
  "deliveryportal-auth-claim-reauth-attempts";

export const MAX_CLAIM_REAUTH_ATTEMPTS = 2;

export const decodeJwtPayload = (
  jwt: string | undefined
): UserClaims | undefined => {
  const parts = jwt?.split(".") ?? [];
  if (parts.length !== 3) {
    return undefined;
  }

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    return JSON.parse(atob(padded)) as UserClaims;
  } catch {
    return undefined;
  }
};

export const getAccessTokenClaims = (
  accessToken: AccessToken | undefined
): UserClaims | undefined => {
  if (!accessToken) {
    return undefined;
  }

  const cachedClaims = accessToken.claims;
  const jwtClaims = decodeJwtPayload(accessToken.accessToken);

  if (cachedClaims && jwtClaims) {
    return { ...cachedClaims, ...jwtClaims };
  }

  return jwtClaims ?? cachedClaims;
};

export const getClaimReauthAttempts = (): number => {
  const raw = sessionStorage.getItem(AUTH_CLAIM_REAUTH_ATTEMPTS_KEY);
  const attempts = Number(raw);
  return Number.isNaN(attempts) ? 0 : attempts;
};

export const incrementClaimReauthAttempts = (): number => {
  const next = getClaimReauthAttempts() + 1;
  sessionStorage.setItem(AUTH_CLAIM_REAUTH_ATTEMPTS_KEY, String(next));
  return next;
};

export const clearClaimReauthAttempts = (): void => {
  sessionStorage.removeItem(AUTH_CLAIM_REAUTH_ATTEMPTS_KEY);
};

export const signOutUser = async (oktaAuth: OktaAuth) => {
  await oktaAuth.signOut();
};

export const hasDeliveryPortalGroupsClaim = (
  claims: UserClaims | undefined
): boolean => {
  if (!claims) {
    return false;
  }
  // @ts-ignore — custom Okta claim carrying app role assignments
  const groups: string[] = claims.DeliveryPortal_groups;
  return Array.isArray(groups) && groups.length > 0;
};

export const getUserRoles = (claims: UserClaims): string[] => {
  // @ts-ignore
  const groups: string[] = claims.DeliveryPortal_groups;
  let allRoles: string[] = [];

  if (groups && groups.length > 0) {
    const allRolesArray: string[][] = [];
    groups.forEach((group) => {
      const splitedGroup = group.split("|");
      allRolesArray.push(splitedGroup);
    });

    //Remove duplicates
    const roleSet = new Set(allRolesArray.flat());
    allRoles = Array.from(roleSet.values());
  }
  return allRoles;
};

const isAdminAuth = (roles: string[]): boolean => {
  //Constains Role
  return roles.indexOf(UserRole.DeliveriesAdmin) > -1;
};

export const useAdminAuthRestrictions = () => {
  const { authState } = useOktaAuth();
  let isAdmin = false;

  if (authState && authState.isAuthenticated) {
    const claims = getAccessTokenClaims(authState.accessToken);
    if (claims) {
      isAdmin = isAdminAuth(getUserRoles(claims));
    }
  }
  return { isAdmin };
};